import { apiGet, apiPost, apiPut, apiDelete } from '../lib/apiClient';
import { auth } from '../lib/authService';
import { generateNextEmployeeId } from './settings';
import {
  encodeContractTypeMarker,
  decodeContractTypeMarker,
  stripContractTypeMarker,
  normalizeContractType,
} from '../utils/contractTypes';

const CONTRACT_TYPES_SETTING_KEY = 'employeeContractTypes';

async function readContractTypeFallback() {
  try {
    const data = await apiGet(`/settings/${CONTRACT_TYPES_SETTING_KEY}`);
    return data && typeof data === 'object' && !Array.isArray(data) ? data : {};
  } catch {
    return {};
  }
}

async function writeContractTypeFallback(employeeId, contractType) {
  if (!employeeId) return;
  const current = await readContractTypeFallback();
  const normalized = normalizeContractType(contractType);
  const next = { ...current };
  if (normalized) next[employeeId] = normalized;
  else delete next[employeeId];
  await apiPut(`/settings/${CONTRACT_TYPES_SETTING_KEY}`, next);
}

function withContractTypePayload(data = {}) {
  if (!data || !Object.prototype.hasOwnProperty.call(data, 'contractType')) return data;
  return {
    ...data,
    contractType: normalizeContractType(data.contractType),
    hrdNotes: encodeContractTypeMarker(data.hrdNotes, data.contractType),
  };
}

function applyContractTypeFallback(employee, fallbackMap = {}) {
  if (!employee) return employee;
  const fromNotes = decodeContractTypeMarker(employee.hrdNotes);
  const contractType = normalizeContractType(
    employee.contractType || fromNotes || fallbackMap[employee.id] || fallbackMap[employee.employeeId] || ''
  );
  const hasMarker = /\[\[CT:/.test(String(employee.hrdNotes || ''));
  const notes = stripContractTypeMarker(employee.hrdNotes);
  return {
    ...employee,
    contractType: contractType || null,
    hrdNotes: hasMarker ? (notes || null) : (employee.hrdNotes ?? null),
  };
}

const normalizeRole = (role) => {
  const value = String(role || '').toLowerCase();
  if (['employee', 'staff', 'karyawan'].includes(value)) return 'karyawan';
  if (['admin', 'administrator', 'superadmin', 'owner'].includes(value)) return 'admin';
  if (value === 'hr') return 'hr';
  if (['magang', 'pkl', 'intern', 'internship'].includes(value)) return 'magang';
  return value || 'karyawan';
};

export const getAllEmployees = async () => {
  try {
    const user = auth.currentUser;
    if (!user) return [];
    const [data, fallback] = await Promise.all([
      apiGet('/employees'),
      readContractTypeFallback(),
    ]);
    return (Array.isArray(data) ? data : []).map((row) => applyContractTypeFallback(row, fallback));
  } catch (error) {
    console.error('Error fetching employees: ', error);
    return [];
  }
};

export const getEmployeeById = async (employeeDocId) => {
  try {
    const [data, fallback] = await Promise.all([
      apiGet(`/employees/${encodeURIComponent(employeeDocId)}`),
      readContractTypeFallback(),
    ]);
    return data ? applyContractTypeFallback(data, fallback) : null;
  } catch (error) {
    if (error.status === 404) {
      console.log('No such employee document!');
      return null;
    }
    console.error('Error fetching employee by document ID: ', error);
    return null;
  }
};

export const addEmployee = async (employeeData) => {
  try {
    const payload = withContractTypePayload({
      ...employeeData,
      employeeId: employeeData.employeeId || await generateNextEmployeeId({
        role: employeeData.role,
        position: employeeData.position,
        department: employeeData.department,
      }),
      role: normalizeRole(employeeData.role),
      isActive: employeeData.isActive ?? true,
      approvalStatus: employeeData.approvalStatus || 'approved',
      authAccountCreated: employeeData.authAccountCreated ?? false,
    });
    const result = await apiPost('/employees', payload);
    if (result?.id && Object.prototype.hasOwnProperty.call(employeeData, 'contractType')) {
      await writeContractTypeFallback(result.id, payload.contractType).catch((err) => {
        console.warn('Gagal menyimpan cadangan jenis kontrak:', err);
      });
    }
    console.log('Karyawan berhasil ditambahkan dengan ID: ', result.id);
    return { success: true, id: result.id, employeeId: result.employeeId || payload.employeeId };
  } catch (error) {
    console.error('Error saat menambahkan karyawan: ', error);
    return { success: false, error };
  }
};

export const updateEmployee = async (id, updatedData) => {
  try {
    const payload = withContractTypePayload(updatedData);
    const result = await apiPut(`/employees/${encodeURIComponent(id)}`, payload);
    if (Object.prototype.hasOwnProperty.call(updatedData, 'contractType')) {
      await writeContractTypeFallback(id, payload.contractType).catch((err) => {
        console.warn('Gagal menyimpan cadangan jenis kontrak:', err);
      });
    }
    return { success: true, employee: applyContractTypeFallback(result?.employee || { id, ...payload }) };
  } catch (error) {
    console.error('Error updating employee: ', error);
    return { success: false, error };
  }
};

function mapAuthRole(role) {
  const normalized = normalizeRole(role);
  if (normalized === 'admin') return 'admin';
  return 'employee';
}

/** Sinkronkan email/password login ke tabel users (bukan hanya data karyawan). */
export async function updateEmployeeAuthCredentials(employeeId, { email, password, role } = {}) {
  const employee = await getEmployeeById(employeeId);
  if (!employee) throw new Error('Data karyawan tidak ditemukan.');

  const uid = employee.uid || employee.userId;
  const lowerEmail = email ? String(email).trim().toLowerCase() : null;
  const authRole = mapAuthRole(role || employee.role);

  if (!uid) {
    if (!lowerEmail || !password) {
      throw new Error('Akun login belum ada. Email dan password wajib diisi untuk membuat akun.');
    }
    await apiPost('/auth/create-employee-user', {
      email: lowerEmail,
      password,
      employeeDocId: employeeId,
      role: authRole,
    });
    return { success: true, created: true };
  }

  const payload = { uid };
  if (lowerEmail) payload.email = lowerEmail;
  if (password) payload.password = password;
  if (role) payload.role = authRole;

  if (Object.keys(payload).length === 1) {
    return { success: true, skipped: true };
  }

  await apiPost('/auth/update-employee-user', payload);
  return { success: true, updated: true };
}

export const toggleEmployeeStatus = async (id, currentStatus, currentIsActive) => {
  try {
    // Coba endpoint khusus toggle status terlebih dahulu
    try {
      const res = await apiPost(`/employees/${encodeURIComponent(id)}/toggle-status`);
      if (res && res.success) return res;
    } catch (toggleErr) {
      // Jika endpoint belum tersedia (misal backend running proses lama/404), fallback ke PUT /employees/:id
      if (toggleErr?.status !== 404 && !toggleErr?.message?.includes('tidak merespons JSON')) {
        throw toggleErr;
      }
    }

    const isCurrentlyActive = ['Active', 'Aktif'].includes((currentStatus || '').trim()) && currentIsActive !== false && currentIsActive !== 0;
    const nextActive = !isCurrentlyActive;
    const nextStatus = nextActive ? 'Aktif' : 'Nonaktif';

    const putRes = await apiPut(`/employees/${encodeURIComponent(id)}`, {
      status: nextStatus,
      isActive: nextActive,
    });

    return {
      success: true,
      isActive: nextActive,
      status: nextStatus,
      employee: putRes?.employee,
    };
  } catch (error) {
    console.error('Error toggling employee status: ', error);
    return { success: false, error };
  }
};

export const deleteEmployee = async (id) => {
  try {
    await apiDelete(`/employees/${encodeURIComponent(id)}`);
    return { success: true };
  } catch (error) {
    console.error('Error deleting employee: ', error);
    return { success: false, error };
  }
};

export const getEmployeeByEmail = async (email) => {
  try {
    const emailStr = (email || '').trim();
    if (!emailStr) return null;

    const data = await apiGet(`/employees/by-email/${encodeURIComponent(emailStr)}`);
    if (data) return data;

    const lowerEmail = emailStr.toLowerCase();
    if (lowerEmail !== emailStr) {
      const lowerData = await apiGet(`/employees/by-email/${encodeURIComponent(lowerEmail)}`);
      if (lowerData) return lowerData;
    }

    console.log('No such employee document with that email!');
    return null;
  } catch (error) {
    console.error('Error fetching employee by email: ', error);
    return null;
  }
};

export const createEmployeeSignup = async (signupData) => {
  try {
    const result = await apiPost('/auth/register', {
      ...signupData,
      email: String(signupData.email || '').toLowerCase(),
    });
    return { success: true, id: result.id, message: result.message };
  } catch (error) {
    console.error('Error saat mendaftar karyawan: ', error);
    const msg = error?.message || error?.data?.error || 'Terjadi kesalahan internal saat mendaftar.';
    throw new Error(msg);
  }
};

export const getPendingSignups = async () => {
  try {
    const user = auth.currentUser;
    if (!user) return [];
    return await apiGet('/employees/pending');
  } catch (error) {
    console.error('Error fetching pending signups: ', error);
    return [];
  }
};

export const approveEmployeeSignup = async (employeeId, additionalData = {}) => {
  try {
    const signup = await getEmployeeById(employeeId);
    const email = String(signup?.email || additionalData.email || '').trim().toLowerCase();
    const role = normalizeRole(additionalData.role || signup?.role || 'karyawan');
    const overridePassword = additionalData.tempPassword || additionalData.password || null;
    let tempPassword = null;
    let uid = signup?.uid || signup?.userId || null;
    let usedSignupPassword = false;

    // Selalu aktifkan/buat akun lewat endpoint approve.
    // Jika tidak ada override password dan akun sudah ada dari signup → pakai password kandidat.
    const accountResult = await apiPost('/auth/create-approved-account', {
      email,
      employeeDocId: employeeId,
      role: role === 'admin' ? 'admin' : 'employee',
      ...(overridePassword ? { password: overridePassword } : {}),
    });
    uid = accountResult.uid || uid;
    tempPassword = accountResult.tempPassword || null;
    usedSignupPassword = !!accountResult.usedSignupPassword;

    await apiPost(`/employees/${encodeURIComponent(employeeId)}/approve`, {
      startDate: additionalData.startDate || new Date().toISOString(),
      ...additionalData,
    });

    return {
      success: true,
      employeeDocId: employeeId,
      uid,
      employeeId: signup?.employeeId || signup?.employee_id,
      tempPassword,
      usedSignupPassword,
    };
  } catch (error) {
    console.error('Error approving employee signup: ', error);
    return { success: false, error };
  }
};

export const rejectEmployeeSignup = async (employeeId, reason = '') => {
  try {
    await apiPost(`/employees/${encodeURIComponent(employeeId)}/reject`, { reason });
    return { success: true };
  } catch (error) {
    console.error('Error rejecting employee signup: ', error);
    return { success: false, error };
  }
};

export async function getEmployeeByEmailSafe(email) {
  try {
    const res = await getEmployeeByEmail(email);
    return res || null;
  } catch (e) {
    console.debug('Employee not found for email:', email);
    return null;
  }
}

export async function getEmployeeByUid(uid) {
  console.log('getEmployeeByUid: searching for uid:', uid);
  if (!uid) return null;
  try {
    const data = await apiGet(`/employees/by-uid/${encodeURIComponent(uid)}`);
    if (!data) {
      console.log('getEmployeeByUid: no employee found for uid:', uid);
      return null;
    }
    console.log('getEmployeeByUid: found employee:', data);
    return data;
  } catch (error) {
    console.error('getEmployeeByUid error:', error);
    return null;
  }
}

export async function getEmployeeByUidSafe(uid) {
  try {
    const res = await getEmployeeByUid(uid);
    return res || null;
  } catch (e) {
    console.debug('Employee not found for uid:', uid);
    return null;
  }
}

const NICKNAME_KEYS = ['nickname', 'nickName', 'namaPanggilan', 'preferredName'];

const hasText = (value) => String(value ?? '').trim().length > 0;

const pickNickname = (emp) => {
  if (!emp) return '';
  for (const key of NICKNAME_KEYS) {
    const value = String(emp[key] ?? '').trim();
    if (value) return value;
  }
  return '';
};

export async function resolveEmployeeForUser(uid, email) {
  try {
    const params = new URLSearchParams();
    if (uid) params.set('uid', uid);
    if (email) params.set('email', email);
    const query = params.toString();
    if (!query) return null;
    return await apiGet(`/employees/resolve?${query}`);
  } catch (error) {
    console.error('resolveEmployeeForUser error:', error);
    return null;
  }
}

export async function healEmployeeNickname(employee) {
  const nickname = pickNickname(employee);
  if (!employee?.id || !nickname) return;

  const needsHeal = !hasText(employee.nickname) || !hasText(employee.nickName) || !hasText(employee.namaPanggilan);
  if (!needsHeal) return;

  try {
    await updateEmployee(employee.id, {
      nickname,
      nickName: nickname,
      namaPanggilan: nickname,
    });
  } catch (error) {
    console.warn('healEmployeeNickname failed:', error);
  }
}
